/**
 * What each theme's feedback sounds like.
 *
 * The *meaning* of a FlashBook's answer sounds is fixed — one steady stroke marks a
 * choice, two rising strokes mean it was right, one falling stroke over a low body
 * means it was wrong — but until now the material was fixed too, and all nine themes
 * played the same marker on the same paper. A learner heard the app, not their book.
 *
 * So each theme declares its own voice, and the voices are deliberately different
 * *materials* rather than the same stroke at nine pitches:
 *
 * | Theme | What it is made of |
 * | --- | --- |
 * | storybook | a pencil in a well-kept hardback: warm, mid, unhurried |
 * | notebook | a soft pencil on exercise paper: broad, dull, and *quiet* |
 * | academy | a fountain pen on good stock: crisp, narrow, with a small chime |
 * | magazine | an editor's marker: fast, bright, assured |
 * | space-adventure | a synth: the answer is a tone that rises or falls, with one wide stroke under it |
 * | laboratory | an instrument: very short, very bright ticks and a pip |
 * | mathematics | chalk on a board: broad, dry, no treble to it |
 * | comic | a fat marker and a zap: loud, low-mid, with a square-wave accent |
 * | heritage | a nib on parchment: slow, warm, wooden |
 *
 * **The notebook's voice is the one that was reported.** Its highlighter came out of
 * the shared recipe — a 2.1kHz band with a 650Hz sweep at the app's full level — and
 * on ruled paper in a quiet room that reads as a squeak on glass: *loud and odd*.
 * Its own voice is therefore the softest here by a wide margin (half the reference
 * level), its bands are the broadest (a low `q` is a dull, papery contact rather than
 * a ringing one), and it has no tone layer at all. Nothing else in this file is
 * allowed to be as quiet: the point is that the pencil is quiet, not that sounds are
 * quiet.
 *
 * The engine plays these (see `playInkVoice` in `#/lib/sounds`), and a test holds
 * every theme to having a voice of its own — see `inkVoices.test.ts`.
 */
import {type InkVoice} from '#/lib/inkVoice'

/** A pencil in a well-kept hardback. Warm, mid, unhurried. */
export const STORYBOOK_INK: InkVoice = {
  mark: [{kind: 'stroke', hz: 2000, sweep: 600, duration: 0.17, q: 0.85}],
  correct: [
    {kind: 'stroke', hz: 1750, sweep: 850, duration: 0.15, q: 0.9},
    {
      kind: 'stroke',
      hz: 2300,
      sweep: 1000,
      duration: 0.19,
      delay: 0.13,
      q: 0.9,
    },
  ],
  wrong: [
    {kind: 'stroke', hz: 1450, sweep: -480, duration: 0.26, q: 0.7},
    {
      kind: 'tone',
      hz: 132,
      to: 80,
      duration: 0.16,
      gain: 0.3,
      waveform: 'sine',
    },
  ],
  gain: 0.4,
}

/**
 * A soft pencil on exercise paper. The quiet one — see the note in this file's
 * header, and the report it answers.
 */
export const NOTEBOOK_INK: InkVoice = {
  mark: [
    {kind: 'stroke', hz: 1150, sweep: 180, duration: 0.19, q: 0.5, gain: 0.55},
  ],
  correct: [
    {kind: 'stroke', hz: 1250, sweep: 420, duration: 0.2, q: 0.5, gain: 0.6},
    {
      kind: 'stroke',
      hz: 1600,
      sweep: 380,
      duration: 0.18,
      delay: 0.16,
      q: 0.5,
      gain: 0.45,
    },
  ],
  // No body under it: a thud is the pen being pressed down, and this theme is a
  // pencil that never presses. It is also the layer that made the old sound feel
  // heavy on a page this light.
  wrong: [
    {kind: 'stroke', hz: 900, sweep: -160, duration: 0.24, q: 0.45, gain: 0.55},
  ],
  gain: 0.2,
}

/** A fountain pen on good stock: crisp and narrow, with a small chime. */
export const ACADEMY_INK: InkVoice = {
  mark: [{kind: 'stroke', hz: 2600, sweep: 520, duration: 0.12, q: 1.6}],
  correct: [
    {kind: 'stroke', hz: 2500, sweep: 900, duration: 0.12, q: 1.7},
    {kind: 'stroke', hz: 3000, sweep: 800, duration: 0.14, delay: 0.11, q: 1.7},
    {
      kind: 'tone',
      hz: 1320,
      to: 1320,
      duration: 0.2,
      delay: 0.1,
      gain: 0.16,
      waveform: 'sine',
    },
  ],
  wrong: [
    {kind: 'stroke', hz: 1900, sweep: -700, duration: 0.22, q: 1.5},
    {
      kind: 'tone',
      hz: 180,
      to: 120,
      duration: 0.18,
      gain: 0.22,
      waveform: 'sine',
    },
  ],
  gain: 0.38,
}

/** An editor's marker: fast and bright, the way a page gets marked up. */
export const MAGAZINE_INK: InkVoice = {
  mark: [{kind: 'stroke', hz: 2400, sweep: 900, duration: 0.13, q: 1.1}],
  correct: [
    {kind: 'stroke', hz: 2200, sweep: 1200, duration: 0.12, q: 1.1},
    {kind: 'stroke', hz: 2900, sweep: 1100, duration: 0.16, delay: 0.1, q: 1.1},
  ],
  wrong: [
    {kind: 'stroke', hz: 1700, sweep: -600, duration: 0.24, q: 0.8},
    {
      kind: 'tone',
      hz: 160,
      to: 96,
      duration: 0.14,
      gain: 0.26,
      waveform: 'triangle',
    },
  ],
  gain: 0.42,
}

/** A synth: the answer moves, and one wide stroke sits under it. */
export const SPACE_ADVENTURE_INK: InkVoice = {
  mark: [{kind: 'stroke', hz: 1200, sweep: 500, duration: 0.18, q: 0.5}],
  correct: [
    {kind: 'stroke', hz: 900, sweep: 700, duration: 0.2, q: 0.45},
    {
      kind: 'tone',
      hz: 520,
      to: 1040,
      duration: 0.3,
      gain: 0.22,
      waveform: 'triangle',
    },
  ],
  wrong: [
    {kind: 'stroke', hz: 900, sweep: -500, duration: 0.28, q: 0.4},
    {
      kind: 'tone',
      hz: 420,
      to: 160,
      duration: 0.34,
      gain: 0.24,
      waveform: 'sawtooth',
    },
  ],
  gain: 0.36,
}

/** An instrument: very short, very bright, and a pip. */
export const LABORATORY_INK: InkVoice = {
  mark: [{kind: 'stroke', hz: 3400, sweep: 200, duration: 0.1, q: 2.2}],
  correct: [
    {kind: 'stroke', hz: 3600, sweep: 400, duration: 0.09, q: 2.4},
    {kind: 'stroke', hz: 4400, sweep: 300, duration: 0.1, delay: 0.09, q: 2.4},
    {
      kind: 'tone',
      hz: 1760,
      to: 1760,
      duration: 0.12,
      delay: 0.08,
      gain: 0.14,
      waveform: 'sine',
    },
  ],
  wrong: [
    {kind: 'stroke', hz: 1400, sweep: -300, duration: 0.14, q: 1.8},
    {
      kind: 'tone',
      hz: 220,
      to: 130,
      duration: 0.16,
      gain: 0.2,
      waveform: 'square',
    },
  ],
  gain: 0.34,
}

/** Chalk on a board: broad, dry, and no treble to it. */
export const MATHEMATICS_INK: InkVoice = {
  mark: [
    {kind: 'stroke', hz: 1500, sweep: 340, duration: 0.2, q: 0.5, gain: 0.7},
  ],
  correct: [
    {kind: 'stroke', hz: 1600, sweep: 600, duration: 0.16, q: 0.5, gain: 0.75},
    {
      kind: 'stroke',
      hz: 2100,
      sweep: 500,
      duration: 0.17,
      delay: 0.12,
      q: 0.5,
      gain: 0.7,
    },
  ],
  wrong: [
    {kind: 'stroke', hz: 1200, sweep: -420, duration: 0.26, q: 0.45, gain: 0.7},
    {
      kind: 'tone',
      hz: 150,
      to: 92,
      duration: 0.18,
      gain: 0.24,
      waveform: 'triangle',
    },
  ],
  gain: 0.38,
}

/** A fat marker and a zap. The loud, low-mid one. */
export const COMIC_INK: InkVoice = {
  mark: [
    {kind: 'stroke', hz: 1700, sweep: 800, duration: 0.14, gain: 0.8, q: 0.7},
  ],
  correct: [
    {kind: 'stroke', hz: 1500, sweep: 1000, duration: 0.13, gain: 0.8, q: 0.7},
    {
      kind: 'stroke',
      hz: 2200,
      sweep: 900,
      duration: 0.16,
      delay: 0.11,
      gain: 0.75,
      q: 0.7,
    },
    {
      kind: 'tone',
      hz: 880,
      to: 1320,
      duration: 0.14,
      delay: 0.06,
      gain: 0.12,
      waveform: 'square',
    },
  ],
  wrong: [
    {kind: 'stroke', hz: 1300, sweep: -700, duration: 0.28, gain: 0.8, q: 0.6},
    {
      kind: 'tone',
      hz: 110,
      to: 66,
      duration: 0.2,
      gain: 0.4,
      waveform: 'square',
    },
  ],
  gain: 0.5,
}

/** A nib on parchment: slow, warm, wooden. */
export const HERITAGE_INK: InkVoice = {
  mark: [
    {kind: 'stroke', hz: 1250, sweep: 240, duration: 0.24, q: 0.7, gain: 0.65},
  ],
  correct: [
    {kind: 'stroke', hz: 1350, sweep: 500, duration: 0.22, q: 0.7, gain: 0.7},
    {
      kind: 'stroke',
      hz: 1800,
      sweep: 420,
      duration: 0.24,
      delay: 0.18,
      q: 0.7,
      gain: 0.6,
    },
  ],
  wrong: [
    {kind: 'stroke', hz: 1000, sweep: -320, duration: 0.32, q: 0.65, gain: 0.7},
    {
      kind: 'tone',
      hz: 150,
      to: 90,
      duration: 0.22,
      gain: 0.26,
      waveform: 'triangle',
    },
  ],
  gain: 0.34,
}

/**
 * The voice of a theme that predates the voice layer, and of anything unknown.
 *
 * Kept here with the rest of the voices rather than in the engine, so that all ten
 * are readable side by side — the whole point of this module is that "every theme
 * has its own" can be *looked at*. It is the original shared recipe: silence would be
 * a worse bug than the sameness this replaced.
 */
export const FALLBACK_INK: InkVoice = {
  mark: [{kind: 'stroke', hz: 2100, sweep: 650, duration: 0.16, q: 0.85}],
  correct: [
    {kind: 'stroke', hz: 1850, sweep: 950, duration: 0.15, q: 0.9},
    {kind: 'stroke', hz: 2450, sweep: 1100, duration: 0.19, delay: 0.13},
  ],
  wrong: [
    {kind: 'stroke', hz: 1500, sweep: -520, duration: 0.26, q: 0.7},
    {
      kind: 'tone',
      hz: 140,
      to: 84,
      duration: 0.15,
      gain: 0.35,
      waveform: 'sine',
    },
  ],
  gain: 0.4,
}
